/**
 * The project page's right-pane state: the selected definition (`?def=`,
 * other query params kept), the open editor, a withdraw's outcome, and
 * whether the viewer's own action opened the pane (its heading then takes
 * focus).
 *
 * An editor with unsaved text is never dropped silently: a list click,
 * `+ New` or the editor's Close while it is dirty waits as `discard`,
 * which the page asks about inline (never `window.confirm`) and runs on
 * `discard.confirm`.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EditorRequest, EditorTarget, Withdrawn } from '../components/DefinitionPane';

export interface PendingDiscard {
  /** What the open editor edits: its name, or null for a new definition. */
  name: string | null;
  confirm: () => void;
  cancel: () => void;
}

export interface ProjectPane {
  selected: string | null;
  editing: EditorTarget | null;
  withdrawn: Withdrawn | null;
  focusPane: boolean;
  discard: PendingDiscard | null;
  select: (fileName: string) => void;
  open: (request: EditorRequest) => void;
  close: () => void;
  saved: (name: string) => void;
  setDirty: (dirty: boolean) => void;
  setWithdrawn: (withdrawn: Withdrawn | null) => void;
}

export function useProjectPane(): ProjectPane {
  const [params, setParams] = useSearchParams();
  const selected = params.get('def');
  const [withdrawn, setWithdrawn] = useState<Withdrawn | null>(null);
  const [editing, setEditing] = useState<EditorTarget | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [focusPane, setFocusPane] = useState(false);

  const setDef = (fileName: string): void => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('def', fileName);
      return next;
    });
  };
  // Runs `action` now, or once the viewer confirms discarding unsaved text.
  const guard = (action: () => void): void => {
    const run = (): void => {
      setDirty(false);
      setFocusPane(true);
      action();
    };
    if (editing !== null && dirty) setPending(() => run);
    else run();
  };
  const name = editing?.kind === 'edit' ? editing.start.name : null;

  return {
    selected,
    editing,
    withdrawn,
    focusPane,
    discard: pending === null ? null : {
      name,
      confirm: () => { setPending(null); pending(); },
      cancel: () => { setPending(null); },
    },
    select: (fileName) => {
      guard(() => {
        setWithdrawn(null);
        setEditing(null);
        setDef(fileName);
      });
    },
    open: (request) => {
      guard(() => { setEditing((prev) => ({ ...request, seq: (prev?.seq ?? 0) + 1 })); });
    },
    close: () => { guard(() => { setEditing(null); }); },
    // A new definition is saved as `<name>.yaml`: select it, so closing the
    // editor shows it once the refetched list has it.
    saved: (saved) => { if (editing?.kind === 'new') setDef(`${saved}.yaml`); },
    setDirty,
    setWithdrawn,
  };
}
